'use client';

import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/rich-text';
import { Loader2, Save, FileUp, X, Type, File as FileIcon } from 'lucide-react';
import type { ProjectDoc } from '@/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DocEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (formData: FormData) => Promise<void>;
    initialData?: ProjectDoc;
    isSaving: boolean;
}

export const DocEditorModal = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    isSaving,
}: DocEditorModalProps) => {
    const [title, setTitle] = React.useState('');
    const [content, setContent] = React.useState('');
    const [attachment, setAttachment] = React.useState<File | null>(null);
    const [activeTab, setActiveTab] = React.useState<string>('text');
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (isOpen) {
            setTitle(initialData?.title || '');
            const isFileDoc = initialData?.project_files === 'attachment';

            setContent(initialData?.project_files || '');
            setAttachment(null);

            if (isFileDoc) {
                setActiveTab('file');
            } else {
                setActiveTab('text');
            }
        }
    }, [isOpen, initialData]);

    const handleSave = async () => {
        if (!title.trim()) return;

        const isFileTab = activeTab === 'file';

        // Validation:
        if (!isFileTab && !content.trim()) return;
        if (isFileTab && !attachment && !initialData?.attachment) return;

        const formData = new FormData();
        formData.append('title', title);

        if (isFileTab) {
            formData.append('project_files', 'attachment');
            if (attachment) {
                formData.append('attachment', attachment);
            }
        } else {
            formData.append('project_files', content);
        }

        await onSave(formData);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChangeHandler}>
            <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden border-border/40 backdrop-blur-2xl">
                <DialogHeader className="px-8 py-6 border-b border-border/10 bg-gradient-to-r from-primary/5 to-transparent">
                    <DialogTitle className="text-2xl tracking-tight">
                        {initialData ? 'Refine Document' : 'Compose New Document'}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                    <div className="space-y-3">
                        <label className="text-sm mb-3">Document Title</label>
                        <Input
                            placeholder="Enter a compelling title..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
                        <TabsList className="grid w-full grid-cols-2 h-12 p-1 bg-muted/30 rounded-xl border border-border/10">
                            <TabsTrigger value="text" className="rounded-lg gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                                <Type className="h-4 w-4" />
                                Rich Text Document
                            </TabsTrigger>
                            <TabsTrigger value="file" className="rounded-lg gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                                <FileIcon className="h-4 w-4" />
                                File Upload
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="text" className="space-y-3 focus-visible:outline-none">
                            <label className="text-sm mb-3">Documentation Content</label>
                            <div className="min-h-[350px] rounded-2xl border border-border/40 bg-background/50 overflow-hidden focus-within:ring-4 focus-within:ring-primary/5 transition-all shadow-inner group">
                                <RichTextEditor
                                    value={content}
                                    onChange={setContent}
                                    placeholder="Start documenting your brilliance..."
                                    className="min-h-[330px] p-2"
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="file" className="space-y-3 focus-visible:outline-none">
                            <label className="text-sm mb-3">Upload Your Asset</label>
                            <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-border/60 rounded-2xl bg-muted/10 hover:bg-muted/20 transition-all cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
                                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <FileUp className="h-8 w-8 text-primary" />
                                </div>
                                <p className="text-lg font-bold italic tracking-tight">
                                    {attachment ? attachment.name : (initialData?.attachment ? initialData.attachment.split('/').pop() : 'Select a file to upload')}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">PDF, DOCX, Images, etc.</p>

                                <Button
                                    type="button"
                                    variant="outline"
                                    className="mt-6"
                                >
                                    Browse Files
                                </Button>
                            </div>

                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                            />

                            {attachment && (
                                <div className="flex justify-center mt-4">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); setAttachment(null); }}
                                    >
                                        <X className="h-4 w-4" />
                                        Clear Selection
                                    </Button>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>

                <DialogFooter className="px-8 py-6 border-t border-border/10 bg-muted/20 backdrop-blur-sm">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Dismiss
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || !title.trim() || (activeTab === 'text' && !content.trim()) || (activeTab === 'file' && !attachment && !initialData?.attachment)}
                    >
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {initialData ? 'Preserve Changes' : 'Publish Document'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    function onOpenChangeHandler(open: boolean) {
        if (!open) onClose();
    }
};
